import React from "react";
import type { UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { BusinessProfileFormData } from "@/types/business-profile";

interface BasicInfoSectionProps {
  register: UseFormRegister<BusinessProfileFormData>;
  errors: FieldErrors<BusinessProfileFormData>;
  watch: UseFormWatch<BusinessProfileFormData>;
  setValue: UseFormSetValue<BusinessProfileFormData>;
  isSubmitting: boolean;
  loading: boolean;
}

const companySizeOptions = [
  { value: "1-10", label: "1-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-100", label: "51-100 employees" },
  { value: "101-500", label: "101-500 employees" },
  { value: "501-1000", label: "501-1000 employees" },
  { value: "1000+", label: "1000+ employees" },
];

const industryOptions = [
  { value: "Technology", label: "Technology" },
  { value: "Healthcare", label: "Healthcare" },
  { value: "Finance", label: "Finance" },
  { value: "Education", label: "Education" },
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Retail", label: "Retail" },
  { value: "Real Estate", label: "Real Estate" },
  { value: "Consulting", label: "Consulting" },
  { value: "Marketing", label: "Marketing" },
  { value: "Other", label: "Other" },
];

export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  register,
  errors,
  watch,
  setValue,
  isSubmitting,
  loading,
}) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">
        Basic Information
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Business Name <span className="text-destructive">*</span></Label>
          <Input
            id="name"
            placeholder="Enter business name"
            {...register("name")}
            error={errors.name?.message}
            disabled={isSubmitting || loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            placeholder="https://example.com"
            {...register("website")}
            error={errors.website?.message}
            disabled={isSubmitting || loading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <textarea
          id="description"
          rows={3}
          placeholder="Describe your business"
          {...register("description")}
          disabled={isSubmitting || loading}
          className="w-full p-3 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-70 bg-background text-foreground placeholder:text-muted-foreground"
        />
        {errors.description && (
          <p className="text-sm text-destructive">
            {errors.description.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="industry">Industry</Label>
          <Select
            value={watch("industry")}
            onValueChange={(value) => setValue("industry", value, { shouldValidate: true })}
            disabled={isSubmitting || loading}
          >
            <SelectTrigger className={errors.industry ? "border-destructive" : ""}>
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent>
              {industryOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.industry && (
            <p className="text-sm text-destructive">
              {errors.industry.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="designation">Designation <span className="text-destructive">*</span></Label>
          <Input
            id="designation"
            placeholder="Your designation"
            {...register("designation")}
            error={errors.designation?.message}
            disabled={isSubmitting || loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="company_size">Company Size</Label>
          <Select
            value={watch("company_size")}
            onValueChange={(value) => setValue("company_size", value, { shouldValidate: true })}
            disabled={isSubmitting || loading}
          >
            <SelectTrigger className={errors.company_size ? "border-destructive" : ""}>
              <SelectValue placeholder="Select company size" />
            </SelectTrigger>
            <SelectContent>
              {companySizeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.company_size && (
            <p className="text-sm text-destructive">
              {errors.company_size.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};